import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, Optional, Output, TemplateRef } from '@angular/core';
import { Router } from '@angular/router';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { MultipleFlagsComponent } from '@frontend/sports/common/ui/colored-icon';
import { RxFor } from '@rx-angular/template/for';
import { trim } from 'lodash-es';

import { ItemComponent } from '../item/item.component';
import { ItemTreeTrackingService } from './item-tree-tracking.service';
import { TreeItem, TreeSeparatorItem } from './item.model';

@Component({
    selector: 'ms-item-tree',
    templateUrl: 'item-tree.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgTemplateOutlet, MultipleFlagsComponent, ItemComponent, RxFor],
})
export class ItemTreeComponent {
    @Input() tree: TreeItem[];
    @Input() level = 0;
    @Input() leafTemplate: TemplateRef<any>;
    @Input() esportsItemTemplate = false;
    @Output() toggle = new EventEmitter<TreeItem>();

    @HostBinding('class') get className(): string {
        return trim(`item-tree item-level-${this.level}`);
    }

    @HostBinding('style') get contentCounts(): Record<string, number> {
        const { items, separators, lines } = this.displayedItemsCount(this.tree, this.level);

        return { '--list-item-count': items, '--separator-item-count': separators, '--top-border-lines-count': lines };
    }

    constructor(
        public sitecore: Sitecore,
        private router: Router,
        @Optional() private trackingService?: ItemTreeTrackingService,
    ) {}

    trackItem(index: number, item: TreeItem): string | number {
        return item.id;
    }

    isSeparator(item: TreeItem): boolean {
        return item instanceof TreeSeparatorItem;
    }

    isLeaf(item: TreeItem): boolean {
        return !item.children.length;
    }

    childToggled(item: TreeItem): void {
        this.toggle.next(item);
    }

    clicked(event: MouseEvent, item: TreeItem): void {
        if (!item.children.length) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        this.toggle.next(item);

        if (this.esportsItemTemplate) {
            this.trackingService?.trackTreeItemClick(item.href!, item.title, item.expanded);
        }
    }

    linkClicked(event: MouseEvent, item: TreeItem): void {
        event.preventDefault();
        event.stopPropagation();

        this.router.navigate([item.href]);

        if (this.esportsItemTemplate) {
            this.trackingService?.trackEsportGoToPage(item.href!, item.title);
        }
    }

    private displayedItemsCount(items: TreeItem[], level: number) {
        const result = { items: 0, separators: 0, lines: 0 };
        for (const item of items) {
            if (this.isSeparator(item)) {
                result.separators += 1;
            } else {
                result.items += 1;
                if (level === 1) {
                    result.lines += 1;
                }
            }
            if (item.expanded && item.children && item.children.length > 0) {
                const childCounts = this.displayedItemsCount(item.children, level + 1);
                result.items += childCounts.items;
                result.separators += childCounts.separators;
                result.lines += childCounts.lines;
            }
        }

        return result;
    }
}
